<script lang="ts">
  /**
   * The menus' top-level destinations: a bottom tab bar on a phone, a left
   * sidebar from the desktop breakpoint up (`layout`, decided by the game,
   * which already owns the breakpoint — TwinTen's `viewport.svelte.ts`).
   *
   * A `<nav>` of buttons with `aria-current="page"` rather than a `tablist`:
   * each entry replaces the whole screen below it, which is navigation, not a
   * tab panel beside its tabs.
   *
   * A tab carries at most one mark: a **badge** when there is a count worth a
   * number (unread messages and requests), otherwise a **dot** when something
   * is simply waiting (today's challenge not played). Neither mark is only
   * colour — a screen reader hears "3 new" or the dot's own label as part of
   * the button's name.
   *
   * In the sidebar, `brand` (a wordmark) sits above the list and `footer`
   * (the profile button) below it; the bar has room for neither.
   *
   * **The rail** (`layout="rail"`, since 1.6.0): a tablet's column of tabs (or
   * a phone's on its side), 5.5rem wide and the full height of its place,
   * each tab its icon over its label as on the bar, with the same badge and
   * dot; `brand` (the game's mark) at the top and `footer` (the round
   * profile button) pinned to the foot. The chosen tab also sits on
   * `--ui-surface-2`, as in the sidebar. A label is never cut short: one too
   * long for the width wraps (at a space, else anywhere). On a short screen
   * (at most 30rem tall: a phone on its side) the spacing tightens so five
   * tabs, the mark and the profile button fit 390px; more tabs than the
   * height holds scroll inside the list, so the brand and the footer stay in
   * place. It pads for `safe-area-inset-bottom`; the top and the sides are
   * its layout's (`AppFrame` keeps them).
   */
  import type { Snippet } from 'svelte';

  import { shellText } from '../i18n.svelte.js';
  import type { ShellTab } from '../types.js';

  interface Props {
    readonly tabs: readonly ShellTab[];
    readonly active: string;
    readonly onselect: (id: string) => void;
    /** `rail` since 1.6.0: a tablet's narrow column of tabs, icons over labels. */
    readonly layout?: 'bar' | 'sidebar' | 'rail';
    readonly brand?: Snippet | undefined;
    readonly footer?: Snippet | undefined;
    /** The landmark's name; the shell's own word for it by default. */
    readonly label?: string | undefined;
    readonly testid?: string | undefined;
    /**
     * Since 1.8.0: `false` leaves the fixed `ui-tab-badge` and `ui-tab-dot` test ids off
     * (`AppFrame` draws a navigation per layout in its first frame, and an id once).
     */
    readonly fixedTestids?: boolean;
  }
  const {
    tabs,
    active,
    onselect,
    layout = 'bar',
    brand,
    footer,
    label,
    testid,
    fixedTestids = true,
  }: Props = $props();

  function badgeOf(tab: ShellTab): number {
    const count = tab.badge ?? 0;
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  }
</script>

<nav
  class="ui-tabs"
  class:ui-tabs--sidebar={layout === 'sidebar'}
  class:ui-tabs--rail={layout === 'rail'}
  aria-label={label ?? shellText('navigation')}
  data-testid={testid}
>
  {#if layout !== 'bar' && brand !== undefined}
    <div class="ui-tabs-brand">{@render brand()}</div>
  {/if}
  <ul class="ui-tabs-list">
    {#each tabs as tab (tab.id)}
      {@const count = badgeOf(tab)}
      {@const dot = count === 0 && tab.dot === true}
      <li class="ui-tabs-item">
        <button
          type="button"
          class="ui-tab"
          class:ui-tab--active={tab.id === active}
          aria-current={tab.id === active ? 'page' : undefined}
          data-testid={tab.testid}
          onclick={() => onselect(tab.id)}
        >
          <span class="ui-tab-icon" aria-hidden="true">
            {@render tab.icon()}
            {#if count > 0}
              <span class="ui-tab-badge" data-testid={fixedTestids ? 'ui-tab-badge' : undefined}
                >{count > 99 ? '99+' : count}</span
              >
            {:else if dot}
              <span class="ui-tab-dot" data-testid={fixedTestids ? 'ui-tab-dot' : undefined}></span>
            {/if}
          </span>
          <span class="ui-tab-label">{tab.label}</span>
          {#if count > 0}
            <span class="ui-visually-hidden">, {shellText('newItems', { count })}</span>
          {:else if dot && tab.dotLabel !== undefined}
            <span class="ui-visually-hidden">, {tab.dotLabel}</span>
          {/if}
        </button>
      </li>
    {/each}
  </ul>
  {#if layout !== 'bar' && footer !== undefined}
    <div class="ui-tabs-footer">{@render footer()}</div>
  {/if}
</nav>

<style>
  .ui-tabs {
    /* The sidebar is the frame's full height with padding inside it: without
       this, a game without a global border-box reset loses its footer. */
    box-sizing: border-box;
    flex: 0 0 auto;
    background: var(--ui-ground);
    border-top: 1px solid var(--ui-line);
    padding-bottom: env(safe-area-inset-bottom);
    font-family: var(--ui-font);
  }

  .ui-tabs-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
  }

  .ui-tabs-item {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
  }

  .ui-tab {
    flex: 1;
    min-width: 0;
    min-height: 3.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    border: 0;
    background: transparent;
    padding: 0.45rem 0.2rem;
    color: var(--ui-ink-2);
    font: var(--ui-text-eyebrow);
  }

  .ui-tab--active {
    color: var(--ui-accent);
  }

  .ui-tab:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: -3px;
    border-radius: var(--ui-radius-sm);
  }

  .ui-tab-icon {
    position: relative;
    display: inline-flex;
    width: 1.5rem;
    height: 1.5rem;
  }

  .ui-tab-icon :global(svg) {
    width: 100%;
    height: 100%;
  }

  .ui-tab-label {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ui-tab-badge {
    position: absolute;
    top: -0.35rem;
    left: 1rem;
    min-width: 1.15rem;
    height: 1.15rem;
    padding: 0 0.25rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--ui-danger);
    color: var(--ui-on-danger);
    font: var(--ui-text-eyebrow);
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .ui-tab-dot {
    position: absolute;
    top: -0.1rem;
    right: -0.25rem;
    width: 0.6rem;
    height: 0.6rem;
    border-radius: 50%;
    background: var(--ui-gold);
    box-shadow: 0 0 0 2px var(--ui-ground);
  }

  /* The sidebar: the same entries, stacked, with room for a label beside
     the icon, the brand above and the footer pinned to the bottom. */
  .ui-tabs--sidebar {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-4);
    border-top: 0;
    border-right: 1px solid var(--ui-line);
    padding: calc(var(--ui-space-5) + env(safe-area-inset-top)) var(--ui-space-3)
      calc(var(--ui-space-4) + env(safe-area-inset-bottom))
      calc(var(--ui-space-3) + env(safe-area-inset-left));
  }

  .ui-tabs--sidebar .ui-tabs-list {
    flex-direction: column;
    gap: var(--ui-space-1);
  }

  .ui-tabs--sidebar .ui-tab {
    flex-direction: row;
    justify-content: flex-start;
    gap: var(--ui-space-3);
    min-height: 3rem;
    padding: 0.55rem 0.85rem;
    border-radius: var(--ui-radius-md);
    font-size: 1rem;
  }

  /* Beside a label, not over one: the count sits off the icon's top right. */
  .ui-tabs--sidebar .ui-tab-badge {
    left: auto;
    right: -0.55rem;
  }

  .ui-tabs--sidebar .ui-tab--active {
    background: var(--ui-surface-2);
  }

  .ui-tabs-brand {
    padding: 0 0.85rem;
  }

  .ui-tabs-footer {
    margin-top: auto;
    padding: 0 0.35rem;
  }

  /* The rail (1.6.0): the bar's tabs, icon over label, stacked down a 5.5rem
     column (a long Dutch word still fits a line); the mark above them and
     the profile button at the foot. */
  .ui-tabs--rail {
    width: 5.5rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-4);
    border-top: 0;
    border-right: 1px solid var(--ui-line);
    padding: var(--ui-space-4) var(--ui-space-1)
      calc(var(--ui-space-4) + env(safe-area-inset-bottom));
  }

  .ui-tabs--rail .ui-tabs-list {
    flex: 0 1 auto;
    min-height: 0;
    overflow-y: auto;
    flex-direction: column;
    gap: var(--ui-space-1);
  }

  .ui-tabs--rail .ui-tabs-item {
    flex: 0 0 auto;
  }

  .ui-tabs--rail .ui-tab {
    border-radius: var(--ui-radius-md);
  }

  /* A nav label is never cut short: too long for the rail, it wraps. */
  .ui-tabs--rail .ui-tab-label {
    overflow: visible;
    white-space: normal;
    overflow-wrap: anywhere;
    text-align: center;
  }

  .ui-tabs--rail .ui-tab--active {
    background: var(--ui-surface-2);
  }

  /* The dot's ring is the colour it sits on. */
  .ui-tabs--rail .ui-tab--active .ui-tab-dot {
    box-shadow: 0 0 0 2px var(--ui-surface-2);
  }

  .ui-tabs--rail .ui-tabs-brand,
  .ui-tabs--rail .ui-tabs-footer {
    display: flex;
    justify-content: center;
    padding: 0;
  }

  /* A short screen (a phone on its side, 390px tall): tighter spacing, each
     tab still a 2.75rem target, so five tabs fit between the mark and the
     profile button. */
  @media (max-height: 30rem) {
    .ui-tabs--rail {
      gap: var(--ui-space-2);
      padding-top: var(--ui-space-2);
      padding-bottom: calc(var(--ui-space-2) + env(safe-area-inset-bottom));
    }

    .ui-tabs--rail .ui-tabs-list {
      gap: 0.125rem;
    }

    .ui-tabs--rail .ui-tab {
      min-height: 2.75rem;
      gap: 0.1rem;
      padding-block: 0.2rem;
    }
  }

  .ui-visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
